(() => {
  if (customElements.get('mc-engraving-designs')) return;
  class EngravingDesigns extends HTMLElement {
    connectedCallback() {
      if (this.cleanup) return;
      const controller = new AbortController();
      const { signal } = controller;
      const cards = Array.from(this.querySelectorAll('[data-card]'));
      const filters = Array.from(this.querySelectorAll('[data-filter]'));
      const grid = this.querySelector('[data-grid]');
      const previous = this.querySelector('[data-previous]');
      const next = this.querySelector('[data-next]');
      const range = this.querySelector('[data-range]');
      const rangeCurrent = this.querySelector('[data-range-current]');
      const rangeTotal = this.querySelector('[data-range-total]');
      const progress = this.querySelector('[data-progress]');
      let scrollFrame = 0;
      let resizeFrame = 0;
      let activeCard = cards[0];
      const visibleCards = () => cards.filter(card => !card.hidden);
      const pageSize = () => {
        const card = visibleCards()[0];
        if (!card) return 1;
        const gap = parseFloat(getComputedStyle(grid).columnGap) || 0;
        return Math.max(1, Math.floor((grid.clientWidth + gap) / (card.getBoundingClientRect().width + gap)));
      };
      const currentIndex = () => {
        const visible = visibleCards();
        const bounds = grid.getBoundingClientRect();
        return visible.reduce((best, card, index) => {
          const rect = card.getBoundingClientRect();
          const distance = Math.abs(rect.left - bounds.left);
          return distance < best.distance ? { index, distance } : best;
        }, { index: 0, distance: Infinity }).index;
      };
      const alignCard = (card, smooth = false) => {
        if (!card) return;
        const trackBounds = grid.getBoundingClientRect();
        const cardBounds = card.getBoundingClientRect();
        grid.scrollTo({ left: grid.scrollLeft + cardBounds.left - trackBounds.left, behavior: smooth && !matchMedia('(prefers-reduced-motion: reduce)').matches ? 'smooth' : 'instant' });
      };
      const updateControls = () => {
        const selected = filters.findIndex(filter => filter.getAttribute('aria-pressed') === 'true');
        if (previous) previous.disabled = selected <= 0;
        if (next) next.disabled = selected >= filters.length - 1;
        if (!range) return;
        const visible = visibleCards();
        const start = currentIndex();
        activeCard = visible[start];
        const end = Math.min(start + pageSize(), visible.length);
        const currentLabel = visible.length ? (end > start + 1 ? (start + 1) + '–' + end : String(start + 1)) : '0';
        if (rangeCurrent.textContent !== currentLabel) rangeCurrent.textContent = currentLabel;
        if (rangeTotal.textContent !== String(visible.length)) rangeTotal.textContent = String(visible.length);
        const max = Math.max(0, grid.scrollWidth - grid.clientWidth);
        const left = Math.max(0, Math.min(grid.scrollLeft, max));
        const fraction = grid.clientWidth / Math.max(1, grid.clientWidth, grid.scrollWidth);
        progress.style.width = `${fraction * 100}%`;
        progress.style.insetInlineStart = `${max ? left / max * (1 - fraction) * 100 : 0}%`;
      };
      const setFilter = button => {
        const selected = button.dataset.filter;
        filters.forEach(filter => filter.setAttribute('aria-pressed', String(filter === button)));
        cards.forEach(card => { card.hidden = selected !== 'all' && !card.dataset.categories.split(/\s+/).includes(selected); });
        const count = visibleCards().length;
        grid.dataset.count = String(count);
        activeCard = visibleCards()[0];
        grid.scrollTo({ left: 0, behavior: 'instant' });
        if (range) alignCard(activeCard);
        const countLabel = count + ' ' + (count === 1 ? this.dataset.countOne : this.dataset.countMany);
        const countElement = this.querySelector('[data-count-label]');
        if (countElement) countElement.textContent = countLabel;
        const category = this.querySelector('[data-category]');
        if (category) category.textContent = button.textContent;
        this.querySelector('[data-live]').textContent = button.textContent + ': ' + countLabel;
        const filterList = button.parentElement;
        const bounds = filterList.getBoundingClientRect();
        const buttonBounds = button.getBoundingClientRect();
        if (buttonBounds.left < bounds.left) filterList.scrollLeft += buttonBounds.left - bounds.left;
        else if (buttonBounds.right > bounds.right) filterList.scrollLeft += buttonBounds.right - bounds.right;
        updateControls();
      };
      const filterList = this.querySelector('.mc-eg__filters');
      filterList.hidden = false;
      const filterBar = filterList.closest('.mc-eg__filter-bar');
      const controls = this.querySelector('[data-carousel-controls]');
      const updateFilterNavigation = () => {
        if (!filterBar || !controls) return;
        const gap = parseFloat(getComputedStyle(filterList).columnGap) || 0;
        const width = filters.reduce((total, button) => total + button.getBoundingClientRect().width, 0) + gap * Math.max(0, filters.length - 1);
        const fits = width <= filterBar.clientWidth + 1;
        filterBar.toggleAttribute('data-filters-fit', fits);
        controls.hidden = fits;
      };
      const filterResize = new ResizeObserver(updateFilterNavigation);
      if (filterBar) [filterBar, ...filters].forEach(element => filterResize.observe(element));
      updateFilterNavigation();
      filters.forEach(button => button.addEventListener('click', () => setFilter(button), { signal }));
      const advanceFilter = direction => {
        const selected = filters.findIndex(filter => filter.getAttribute('aria-pressed') === 'true');
        const button = filters[selected + direction];
        if (button) setFilter(button);
      };
      previous?.addEventListener('click', () => advanceFilter(-1), { signal });
      next?.addEventListener('click', () => advanceFilter(1), { signal });
      if (this.classList.contains('mc-eg--featured')) {
        let drag = null;
        let suppressClick = false;
        grid.addEventListener('dragstart', event => event.preventDefault(), { signal });
        grid.addEventListener('pointerdown', event => {
          suppressClick = false;
          if (event.pointerType !== 'mouse' || event.button !== 0 || grid.scrollWidth <= grid.clientWidth) return;
          drag = { id: event.pointerId, x: event.clientX, scroll: grid.scrollLeft, started: false };
        }, { signal });

        window.addEventListener('pointermove', event => {
          if (event.pointerId !== drag?.id) return;
          const distance = event.clientX - drag.x;
          if (!drag.started) {
            if (Math.abs(distance) < 6) return;
            drag.started = true;
            grid.classList.add('is-mouse-dragging');
            grid.setPointerCapture(event.pointerId);
          }
          grid.scrollLeft = drag.scroll - distance;
        }, { signal });

        const endDrag = event => {
          if (event.pointerId !== drag?.id) return;
          const started = drag.started;
          drag = null;
          if (!started) return;
          suppressClick = true;
          const card = visibleCards()[currentIndex()];
          grid.classList.remove('is-mouse-dragging');
          alignCard(card, true);
        };
        window.addEventListener('pointerup', endDrag, { signal });
        window.addEventListener('pointercancel', endDrag, { signal });
        grid.addEventListener('lostpointercapture', endDrag, { signal });
        grid.addEventListener('click', event => {
          if (!suppressClick || event.detail === 0) return;
          suppressClick = false;
          event.preventDefault();
          event.stopImmediatePropagation();
        }, { capture: true, signal });
      }
      grid.addEventListener('scroll', () => {
        cancelAnimationFrame(scrollFrame);
        scrollFrame = requestAnimationFrame(updateControls);
      }, { passive: true, signal });
      const resize = new ResizeObserver(() => {
        if (!range) return;
        const selected = activeCard;
        cancelAnimationFrame(resizeFrame);
        resizeFrame = requestAnimationFrame(() => {
          alignCard(selected);
          updateControls();
        });
      });
      resize.observe(grid);
      const onBlockSelect = event => {
        const card = event.target.closest('[data-card]');
        if (!card || !this.contains(card)) return;
        setFilter(filters[0]);
        alignCard(card);
        updateControls();
      };
      this.addEventListener('shopify:block:select', onBlockSelect, { signal });
      updateControls();
      this.cleanup = () => {
        controller.abort();
        grid.classList.remove('is-mouse-dragging');
        resize.disconnect();
        filterResize.disconnect();
        cancelAnimationFrame(scrollFrame);
        cancelAnimationFrame(resizeFrame);
      };
    }
    disconnectedCallback() {
      this.cleanup?.();
      this.cleanup = null;
    }
  }
  customElements.define('mc-engraving-designs', EngravingDesigns);
})();
